import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import { grey, indigo, lightGreen, lime, orange, purple, teal, pink, yellow, red, cyan } from '@mui/material/colors'
import { useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import React from 'react'

import { ReactComponent as ContainerDeliveryTruck } from 'assets/methods/ContainerDeliveryTruck.svg'
import { ReactComponent as DumpTruck } from 'assets/methods/DumpTruck.svg'
import { ReactComponent as FlatBed } from 'assets/methods/FlatBed.svg'
import { ReactComponent as FrontLoad } from 'assets/methods/FrontLoad.svg'
import { ReactComponent as Grapple } from 'assets/methods/Grapple.svg'
import { ReactComponent as Hoist } from 'assets/methods/Hoist.svg'
import { ReactComponent as Hook } from 'assets/methods/Hook.svg'
import { ReactComponent as RearLoad } from 'assets/methods/RearLoad.svg'
import { ReactComponent as SideLoad } from 'assets/methods/SideLoad.svg'
import { ReactComponent as TankerTruck } from 'assets/methods/TankerTruck.svg'
import { ReactComponent as Van } from 'assets/methods/Van.svg'
import { METHOD_TYPES } from 'settings/constants/methods'
import { normalizeCasing } from 'utils/string'

const { SIDE_LOAD, FRONT_LOAD, REAR_LOAD, DUMP_TRUCK, GRAPPLE, HOIST, HOOK, TANKER, CONTAINER_DELIVERY, VAN, FLAT_BED } = METHOD_TYPES

export const METHOD_TO_ICON_MAP: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  [SIDE_LOAD]: SideLoad,
  [FRONT_LOAD]: FrontLoad,
  [REAR_LOAD]: RearLoad,
  [DUMP_TRUCK]: DumpTruck,
  [GRAPPLE]: Grapple,
  [HOIST]: Hoist,
  [HOOK]: Hook,
  [TANKER]: TankerTruck,
  [CONTAINER_DELIVERY]: ContainerDeliveryTruck,
  [VAN]: Van,
  [FLAT_BED]: FlatBed,
}

const METHOD_TO_BG_COLOR_MAP: Record<string, string> = {
  [SIDE_LOAD]: teal[100],
  [FRONT_LOAD]: orange[100],
  [REAR_LOAD]: purple[100],
  [DUMP_TRUCK]: grey[500],
  [HOOK]: indigo[100],
  [HOIST]: lime[100],
  [GRAPPLE]: lightGreen[100],
  [TANKER]: pink[100],
  [CONTAINER_DELIVERY]: yellow[100],
  [VAN]: red[100],
  [FLAT_BED]: cyan[100],
}

type MethodChipProps = {
  methodType?: string
  label?: string
  size?: 'small' | 'medium'
  [key: string]: unknown
}

const MethodChip = ({ methodType, label, size, ...rest }: MethodChipProps) => {
  const theme = useTheme()
  const methodTypeUpperCase = methodType?.toUpperCase() ?? ''
  const Icon = METHOD_TO_ICON_MAP[methodTypeUpperCase] ?? SideLoad
  const bgcolor = METHOD_TO_BG_COLOR_MAP[methodTypeUpperCase] ?? grey[500]
  const capitalizedLabel = normalizeCasing(label || methodType)

  return (
    <Chip
      avatar={
        <Avatar sx={{ backgroundColor: `${theme.palette.background.black}61` }}>
          {/* eslint-disable-next-line no-restricted-syntax -- 14px is this avatar's existing size; the theme scale's nearest is 20px */}
          <SvgIcon sx={{ color: theme.palette.common.white, fontSize: 14 }}>
            <Icon />
          </SvgIcon>
        </Avatar>
      }
      label={capitalizedLabel}
      size={size ?? 'medium'}
      sx={{ bgcolor, '&:hover': { bgcolor } }}
      variant="filled"
      {...rest}
    />
  )
}

export default MethodChip
